import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { captureFirstTouch } from "@/lib/attribution";
captureFirstTouch();
import { Suspense, lazy } from "react";
import Layout from "@/components/site/Layout";
import Home from "@/pages/Home";
import { Privacy, Terms, Sitemap, NotFound } from "@/pages/Static";

// Code-split all secondary routes to keep the homepage lean.
const About = lazy(() => import("@/pages/About"));
const FounderPage = lazy(() => import("@/pages/Founder"));
const Services = lazy(() => import("@/pages/Services"));
const ServiceDetail = lazy(() => import("@/pages/ServiceDetail"));
const ServiceLocation = lazy(() => import("@/pages/ServiceLocation"));
const Work = lazy(() => import("@/pages/Work"));
const ProjectDetail = lazy(() => import("@/pages/ProjectDetail"));
const CaseStudiesList = lazy(() => import("@/pages/CaseStudies"));
const CaseStudyDetail = lazy(() => import("@/pages/CaseStudyDetail"));
const InsightsList = lazy(() => import("@/pages/InsightsList"));
const InsightDetail = lazy(() => import("@/pages/InsightDetail"));
const IndustriesList = lazy(() => import("@/pages/IndustriesList"));
const IndustryDetail = lazy(() => import("@/pages/IndustryDetail"));
const IndustryLocation = lazy(() => import("@/pages/IndustryLocation"));
const LocationsList = lazy(() => import("@/pages/LocationsList"));
const LocationDetail = lazy(() => import("@/pages/LocationDetail"));
const Contact = lazy(() => import("@/pages/Contact"));
const BookMeeting = lazy(() => import("@/pages/BookMeeting"));
const LandingPage = lazy(() => import("@/landing/LandingPage"));
const BlogList = lazy(() => import("@/pages/BlogList"));
const BlogDetail = lazy(() => import("@/pages/BlogDetail"));
const QAList = lazy(() => import("@/pages/QAList"));
const QADetail = lazy(() => import("@/pages/QADetail"));

// Legal / policy pages
const PaymentPolicy = lazy(() => import("@/pages/legal/PaymentPolicy"));
const RefundPolicy = lazy(() => import("@/pages/legal/RefundPolicy"));
const CancellationPolicy = lazy(() => import("@/pages/legal/CancellationPolicy"));
const DeliveryPolicy = lazy(() => import("@/pages/legal/DeliveryPolicy"));
const Consent = lazy(() => import("@/pages/legal/Consent"));
const Compliance = lazy(() => import("@/pages/legal/Compliance"));
const Pricing = lazy(() => import("@/pages/legal/Pricing"));
const Pay = lazy(() => import("@/pages/Pay"));
const AdminApp = lazy(() => import("@/admin/AdminApp"));

function RouteFallback() {
  return <div className="min-h-[60vh] w-full bg-ink-950" aria-hidden />;
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <Routes>
            <Route
              path="/admin/*"
              element={
                <Suspense fallback={<RouteFallback />}>
                  <Toaster position="top-right" theme="dark" richColors />
                  <AdminApp />
                </Suspense>
              }
            />
            <Route
              path="*"
              element={
          <Layout>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/founder" element={<FounderPage />} />

                <Route path="/services" element={<Services />} />
                <Route path="/services/:slug" element={<ServiceDetail />} />
                <Route path="/services/:serviceSlug/:locationSlug" element={<ServiceLocation />} />

                <Route path="/work" element={<Work />} />
                <Route path="/work/:slug" element={<ProjectDetail />} />
                <Route path="/projects/:slug" element={<ProjectDetail />} />

                <Route path="/case-studies" element={<CaseStudiesList />} />
                <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />

                <Route path="/insights" element={<InsightsList />} />
                <Route path="/insights/:slug" element={<InsightDetail />} />

                <Route path="/blog" element={<BlogList />} />
                <Route path="/blog/:slug" element={<BlogDetail />} />
                <Route path="/questions" element={<QAList />} />
                <Route path="/questions/:slug" element={<QADetail />} />

                <Route path="/industries" element={<IndustriesList />} />
                <Route path="/industries/:slug" element={<IndustryDetail />} />
                <Route path="/industries/:industrySlug/:locationSlug" element={<IndustryLocation />} />

                <Route path="/locations" element={<LocationsList />} />
                <Route path="/locations/:slug" element={<LocationDetail />} />

                <Route path="/contact" element={<Contact />} />
                <Route path="/book-meeting" element={<BookMeeting />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/payment-policy" element={<PaymentPolicy />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/cancellation-policy" element={<CancellationPolicy />} />
                <Route path="/delivery-policy" element={<DeliveryPolicy />} />
                <Route path="/consent" element={<Consent />} />
                <Route path="/compliance" element={<Compliance />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/pay" element={<Pay />} />
                <Route path="/sitemap" element={<Sitemap />} />
                <Route path="/:slug" element={<LandingPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </Layout>
              }
            />
          </Routes>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
